import React from 'react';
import { TravelGroup } from '../../types/itinerary';
import { 
  UserCircle2, Users2, Baby, Heart, Briefcase 
} from 'lucide-react';

interface TravelGroupSelectorProps {
  selected?: TravelGroup;
  onChange: (group: TravelGroup) => void;
}

const GROUPS: Array<{
  type: TravelGroup;
  label: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = [
  {
    type: 'Solo traveler',
    label: 'Solo traveler',
    icon: <UserCircle2 />,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-500'
  },
  {
    type: 'Friends',
    label: 'Friends',
    icon: <Users2 />,
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-500'
  },
  {
    type: 'Family with kids',
    label: 'Family with kids',
    icon: <Baby />,
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-500'
  },
  {
    type: 'Couple',
    label: 'Couple',
    icon: <Heart />,
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
    borderColor: 'border-pink-500'
  },
  {
    type: 'Business trip',
    label: 'Business trip',
    icon: <Briefcase />,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-500'
  }
];

const textStyles = {
  // Base text styles
  base: {
    fontFamily: 'var(--chat-font-family)',
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-normal)',
    lineHeight: 'var(--chat-line-height)',
    color: 'var(--planner-text-primary)'
  },
  // Header styles
  h2: {
    fontSize: 'var(--planner-heading-size)',
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  h3: {
    fontSize: 'var(--planner-subheading-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  h4: {
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  // Text sizes
  large: {
    fontSize: 'var(--planner-subheading-size)'
  },
  small: {
    fontSize: 'var(--planner-small-size)'
  },
  xs: {
    fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
  },
  // Font weights
  medium: {
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  semibold: {
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  // Colors
  secondary: {
    color: 'var(--planner-text-secondary)'
  },
  tertiary: {
    color: 'var(--planner-text-tertiary)'
  }
};

export function TravelGroupSelector({ selected, onChange }: TravelGroupSelectorProps) {
  return (
    <div className="space-y-4">
      <label 
        className="block"
        style={{
          ...textStyles.base,
          ...textStyles.regular,
          ...textStyles.regular,
          marginBottom: '0.5rem'
        }}
      >
        Who are you traveling with?
      </label>
      <div className="grid grid-cols-2 gap-4">
        {GROUPS.map(({ type, label, icon, bgColor, textColor, borderColor }) => {
          const isSelected = selected === type;
          
          // Determine icon color
          const iconColorClass = isSelected 
            ? textColor.replace('text-', 'text-') // Keep the same color but more vibrant
            : 'text-gray-400';
            
          return (
            <button
              key={type}
              onClick={() => onChange(type)}
              className={`flex items-center justify-start gap-3 px-4 py-2 rounded-full border transition-all whitespace-nowrap ${
                isSelected 
                  ? `${bgColor} ${borderColor} ${textColor} shadow-sm`
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              style={{
                fontFamily: 'var(--chat-font-family)',
                fontSize: 'var(--planner-body-size)',
                lineHeight: 'var(--chat-line-height)',
                width: '100%'
              }}
            >
              <span 
                className={iconColorClass}
                style={{
                  width: 'var(--planner-icon-size-md)',
                  height: 'var(--planner-icon-size-md)',
                  display: 'flex',
                  alignItems: 'left',
                  justifyContent: 'center'
                }}
              >
                {React.cloneElement(icon as React.ReactElement, {
                  style: { width: 'var(--planner-icon-size-md)', height: 'var(--planner-icon-size-md)' }
                })}
              </span>
              <span 
                className="block whitespace-nowrap"
                style={{
                  ...textStyles.base,
                  ...textStyles.regular,
                  ...textStyles.regular,
                  color: isSelected ? undefined : 'var(--planner-text-primary)'
                }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}